import { computed, makeObservable } from 'mobx';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Highlighter, type HighlightMatch, type HighlightPart } from '../../services/util/highlighter';
import { InputState } from '../input/input.state';
import { MenuComponent } from '../menu/menu';
import { type MenuItem, MenuItemComponent } from '../menu/menu.item';
import { IMithrilComponent } from '../mithril.component';
import { addStyles } from '../theme';
import { CSS, Styles } from './autocomplete.menu.style';

addStyles(Styles);

const DEFAULT_MAX_RESULTS = 5;
const emptyStr = (str) => str == null || str.trim() === '';

export interface SelectAutoCompleteOption {
	id: string;
	value: string;
	/** Optional extended search text. If present, filtering matches against this instead of value, while display still uses value. */
	searchValue?: string;
}

export interface AutoCompleteMenuCtx {
	inputState: InputState<string>;
	options: () => SelectAutoCompleteOption[];
	onselect: (option: SelectAutoCompleteOption) => void;
	optionView?: (option: SelectAutoCompleteOption, nameParts: MithrilVNode[]) => MenuItem | null;
	maxResults?: number;
	/** Show a option of "(new)" */
	showNew?: boolean;
}

export class AutoCompleteMenu extends IMithrilComponent {
	ctx: AutoCompleteMenuCtx;
	menu: MenuComponent;

	constructor(ctx: AutoCompleteMenuCtx) {
		super();
		makeObservable(this);

		this.ctx = ctx;
		this.menu = new MenuComponent({
			items: () => this.items,
			open: () => this.canOpen(),
			className: CSS.container,
		});
	}

	get nodes(): MithrilVNode {
		return this.menu.view();
	}

	private canOpen(): boolean {
		return this.ctx.inputState.focused && this.items.length > 0;
	}

	isOpen(): boolean {
		return this.menu.isOpen;
	}

	@computed get items(): MenuItem[] {
		const { inputState, maxResults = DEFAULT_MAX_RESULTS } = this.ctx;
		const items = this.ctx.options();

		if (emptyStr(inputState.value)) {
			return items
				.slice(0, maxResults)
				.map((val) => this.createMenuItem([{ highlighted: false, content: val.value }], val));
		}

		let hasMatch = false;
		const highlighted: { match: HighlightMatch; item: SelectAutoCompleteOption }[] = [];
		for (const item of items) {
			const searchMatch = Highlighter.highlight(item.searchValue ?? item.value, inputState.value);
			const isFullMatch = searchMatch.parts.length === 1 && searchMatch.parts[0].highlighted === true;

			if (searchMatch.parts.length > 1 || isFullMatch) {
				// If searchValue is set, re-highlight against the display value for nameParts
				const displayParts =
					item.searchValue != null ? Highlighter.highlight(item.value, inputState.value).parts : searchMatch.parts;
				highlighted.push({ match: { score: searchMatch.score, parts: displayParts }, item });
				if (isFullMatch) {
					// If there is only one part it may fully match the input string
					hasMatch = true;
				}
			}
		}

		const options = highlighted
			.sort((a, b) => b.match.score - a.match.score)
			.slice(0, maxResults)
			.map((val) => this.createMenuItem(val.match.parts, val.item));

		if (this.ctx.showNew && hasMatch === false) {
			const node = this.createMenuItem(
				[
					{ highlighted: true, content: inputState.value },
					{ highlighted: false, content: ' ( new )' },
				],
				{ id: null, value: inputState.value }
			);
			options.push(node);
		}

		return options;
	}

	private createMenuItem(parts: HighlightPart[], option: SelectAutoCompleteOption): MenuItem {
		const text: MithrilVNode[] = parts.map((part) =>
			m('span', { className: part.highlighted ? CSS.highlighted : '' }, part.content)
		);
		if (this.ctx.optionView != null) {
			return this.ctx.optionView(option, text);
		}

		return {
			view: MenuItemComponent.view(text),
			action: () => this.ctx.onselect(option),
		};
	}
}
